import { FC, ReactNode, createContext, useContext, useState } from "react";

// mui
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";

type LoadingContextType = {
  loading: boolean;
  loadingMessage?: string;
  loadingWrapper: (func: () => Promise<void>, message?: string) => void;
  setLoading: (loading: boolean, message?: string) => void;
};

const LoadingContext = createContext<LoadingContextType>({
  loading: false,
  loadingMessage: undefined,
  loadingWrapper: () => {},
  setLoading: () => {},
});

export const LoadingProvider = ({ children }: { children: ReactNode }) => {
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<string | undefined>(undefined);

  const loadingWrapper = async (func: () => Promise<void>, message?: string) => {
    try {
      setLoading(true);
      setLoadingMessage(message);
      await func();
    } finally {
      setLoading(false);
      setLoadingMessage(undefined);
    }
  };

  const setLoadingWithMessage = (loading: boolean, message?: string) => {
    setLoading(loading);
    setLoadingMessage(message);
  };

  return (
    <LoadingContext.Provider
      value={{
        loading,
        loadingMessage,
        loadingWrapper,
        setLoading: setLoadingWithMessage,
      }}
    >
      {children}
    </LoadingContext.Provider>
  );
};

export const useLoading = () => useContext(LoadingContext);

export const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * loadingWrapper 内で window.location.href 等によるページ遷移を行う場合、
 * 遷移完了前に finally が発火して loading が false に戻るのを防ぐために使用する。
 * ページアンロード時に JS コンテキストごと破棄されるため、永続的な pending は問題にならない。
 *
 * @example
 * await loadingWrapper(async () => {
 *   await saveData();
 *   await sleep(1500);
 *   window.location.href = "/next-page";
 *   await blockUntilPageUnload();
 * });
 */
export const blockUntilPageUnload = () => new Promise<never>(() => {});

export const Loading: FC = () => {
  const { loading, loadingMessage } = useLoading();
  return (
    <Backdrop
      sx={{
        color: "#fff",
        zIndex: (theme) => theme.zIndex.modal + 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
      open={loading}
    >
      <CircularProgress color="inherit" size={loadingMessage ? 60 : undefined} sx={{ mb: loadingMessage ? 2 : 0 }} />
      {loadingMessage && (
        <Typography variant="h6" component="div" sx={{ textAlign: "center" }}>
          {loadingMessage}
        </Typography>
      )}
    </Backdrop>
  );
};
